import type { Identifier, RaRecord } from 'react-admin';

export const BILLING_FEATURES_RESOURCE = 'billingFeatures';
export const BILLING_PLANS_RESOURCE = 'billingPlans';
export const BILLING_PLAN_FEATURES_RESOURCE = 'billingPlanFeatures';
export const BILLING_COUNTRY_CURRENCIES_RESOURCE = 'billingCountryCurrencies';
export type BillingRecordId = Identifier;

export type BillingFeatureDataType = 1 | 2 | 3;

export type BillingPlanPrice = {
    countryCode: string;
    priceAmount: number;
    isActive: boolean;
};

export type BillingFeature = RaRecord & {
    id: BillingRecordId;
    key: string;
    name: string;
    dataType: BillingFeatureDataType;
    description: string;
    updatedAt: string;
};

export type BillingPlan = RaRecord & {
    id: BillingRecordId;
    name: string;
    description: string;
    imageUrl: string;
    durationDays: number;
    isActive: boolean;
    prices: BillingPlanPrice[];
    updatedAt: string;
};

export type BillingPlanFeature = RaRecord & {
    id: BillingRecordId;
    planId: BillingRecordId;
    featureId: BillingRecordId;
    value: string;
    updatedAt: string;
};

export type BillingCountryCurrency = RaRecord & {
    id: BillingRecordId;
    countryCode: string;
    countryName: string;
    currency: string;
    isAirwallexSupported: boolean;
    updatedAt: string;
};

export const billingFeatureDataTypeLabels: Record<BillingFeatureDataType, string> = {
    1: 'Boolean',
    2: 'Integer',
    3: 'String',
};

export function getBillingFeatureLabel(feature: Pick<BillingFeature, 'key' | 'name'>): string {
    const explicitName = String(feature.name ?? '').trim();
    if (explicitName) {
        return explicitName;
    }

    return String(feature.key ?? '')
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

const now = new Date().toISOString();

export function normalizeBillingPlan(
    plan: BillingPlan & {
        basePriceUsd?: number;
        billingCountryCode?: string;
        billingPrice?: number;
    },
): BillingPlan {
    const prices = Array.isArray(plan.prices) ? plan.prices : [];
    if (prices.length > 0) {
        return {
            ...plan,
            prices: prices.map((price) => ({
                countryCode: String(price.countryCode ?? '').toUpperCase(),
                priceAmount: Number(price.priceAmount ?? 0),
                isActive: Boolean(price.isActive),
            })),
        };
    }

    if (typeof plan.billingPrice === 'number') {
        return {
            ...plan,
            prices: [
                {
                    countryCode: String(plan.billingCountryCode ?? 'US').toUpperCase(),
                    priceAmount: plan.billingPrice,
                    isActive: true,
                },
            ],
        };
    }

    if (typeof plan.basePriceUsd === 'number') {
        return {
            ...plan,
            prices: [
                {
                    countryCode: 'US',
                    priceAmount: plan.basePriceUsd,
                    isActive: true,
                },
            ],
        };
    }

    return {
        ...plan,
        prices: [],
    };
}

export function normalizeBillingPlans(
    plans: Array<BillingPlan & { basePriceUsd?: number; billingCountryCode?: string; billingPrice?: number }>,
): BillingPlan[] {
    return plans.map(normalizeBillingPlan);
}

export const seedBillingFeatures: BillingFeature[] = [
    {
        id: 1,
        key: 'ai_scoring',
        name: 'AI Scoring',
        dataType: 1,
        description: 'Enable automated scoring and feedback workflows.',
        updatedAt: now,
    },
    {
        id: 2,
        key: 'max_mock_tests',
        name: 'Mock Tests Limit',
        dataType: 2,
        description: 'Maximum number of mock tests available for the subscription.',
        updatedAt: now,
    },
    {
        id: 3,
        key: 'support_sla',
        name: 'Support SLA',
        dataType: 3,
        description: 'Service response target shown to customers and support staff.',
        updatedAt: now,
    },
];

export const seedBillingPlans: BillingPlan[] = [
    {
        id: 1,
        name: 'Starter Monthly',
        description: 'Entry plan for learners who need a smaller monthly practice allowance.',
        imageUrl: '',
        durationDays: 30,
        isActive: true,
        prices: [
            {
                countryCode: 'US',
                priceAmount: 49.99,
                isActive: true,
            },
            {
                countryCode: 'AU',
                priceAmount: 79,
                isActive: true,
            },
            {
                countryCode: 'IN',
                priceAmount: 2999,
                isActive: true,
            },
        ],
        updatedAt: now,
    },
    {
        id: 2,
        name: 'Pro Monthly',
        description: 'Higher-volume monthly access for regular practice and premium support.',
        imageUrl: '',
        durationDays: 30,
        isActive: true,
        prices: [
            {
                countryCode: 'US',
                priceAmount: 69.99,
                isActive: true,
            },
            {
                countryCode: 'AU',
                priceAmount: 109,
                isActive: true,
            },
            {
                countryCode: 'IN',
                priceAmount: 3999,
                isActive: true,
            },
        ],
        updatedAt: now,
    },
    {
        id: 3,
        name: 'Enterprise Annual',
        description: 'Annual plan designed for institutions and larger learner cohorts.',
        imageUrl: '',
        durationDays: 365,
        isActive: true,
        prices: [
            {
                countryCode: 'US',
                priceAmount: 499,
                isActive: true,
            },
            {
                countryCode: 'AU',
                priceAmount: 769,
                isActive: true,
            },
            {
                countryCode: 'IN',
                priceAmount: 29999,
                isActive: true,
            },
        ],
        updatedAt: now,
    },
];

export const seedBillingCountryCurrencies: BillingCountryCurrency[] = [
    {
        id: 1,
        countryCode: 'US',
        countryName: 'United States',
        currency: 'USD',
        isAirwallexSupported: true,
        updatedAt: now,
    },
    {
        id: 2,
        countryCode: 'AU',
        countryName: 'Australia',
        currency: 'AUD',
        isAirwallexSupported: true,
        updatedAt: now,
    },
    {
        id: 3,
        countryCode: 'IN',
        countryName: 'India',
        currency: 'INR',
        isAirwallexSupported: false,
        updatedAt: now,
    },
];

export const seedBillingPlanFeatures: BillingPlanFeature[] = [
    {
        id: 1,
        planId: 1,
        featureId: 1,
        value: 'true',
        updatedAt: now,
    },
    {
        id: 2,
        planId: 1,
        featureId: 2,
        value: '5',
        updatedAt: now,
    },
    {
        id: 3,
        planId: 1,
        featureId: 3,
        value: '48h',
        updatedAt: now,
    },
    {
        id: 4,
        planId: 2,
        featureId: 1,
        value: 'true',
        updatedAt: now,
    },
    {
        id: 5,
        planId: 2,
        featureId: 2,
        value: '25',
        updatedAt: now,
    },
    {
        id: 6,
        planId: 2,
        featureId: 3,
        value: '24h',
        updatedAt: now,
    },
    {
        id: 7,
        planId: 3,
        featureId: 1,
        value: 'true',
        updatedAt: now,
    },
    {
        id: 8,
        planId: 3,
        featureId: 2,
        value: 'Unlimited',
        updatedAt: now,
    },
    {
        id: 9,
        planId: 3,
        featureId: 3,
        value: '4h',
        updatedAt: now,
    },
];